/**
 * Tourmaline — Social Media & Content console (Round 48 · Workforce v2, Phase 4).
 * The week's posts with their pictures, what is waiting in Decisions and what
 * is ready to publish by hand (copy the caption, download the picture, mark it
 * posted), each platform's switch and mode, the daily-plan settings, and the
 * clip releases that decide which members may be named.
 *
 * Round 49: Connect Facebook & Instagram (Facebook's own sign-in, then pick the
 * Page), choose how a connected platform posts, Publish now, Disconnect, and
 * the likes / comments of what Tourmaline published.
 *
 * Round 56: tabs — Posts, Studio (make a post from the Brand Kit, Motion),
 * Brand Kit (the brief, the files, what it still needs), Platforms & settings.
 * ?tab=studio|kit opens a tab straight from a notification.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../AdminApp';
import { API_BASE_URL } from '../../config';
import { toast } from 'sonner';
import { Share2, RefreshCw, Copy, Download, CheckCircle, X, Search, Lock, Inbox, Sparkles, AlertTriangle, Link2, Send, Clapperboard, BookOpen, Settings2, LayoutList, Scissors } from 'lucide-react';
import TourmalineStudio from './TourmalineStudio';
import BrandKitPanel from './BrandKitPanel';
import ClipsPanel from './ClipsPanel';   // Round 65: clips from lives

const BASE = '/api/admin/workforce/tourmaline';

const POST_STATUS = {
  proposed: ['Waiting in Decisions', 'bg-amber-100 text-amber-800'],
  approved: ['Approved', 'bg-emerald-100 text-emerald-800'],
  done: ['Done', 'bg-gray-100 text-gray-600'],
  rejected: ['Skipped', 'bg-gray-100 text-gray-500'],
  expired: ['Expired', 'bg-gray-100 text-gray-500'],
  blocked: ['Blocked', 'bg-red-100 text-red-700'],
};
const PLATFORM_STATUS = {
  waiting: 'waiting for approval', ready: 'ready to post', scheduled: 'scheduled', publishing: 'publishing…',
  posted: 'posted', skipped: 'skipped', failed: 'failed', blocked: 'blocked',
};
const MODE_LABEL = { draft_only: 'You post it', propose: 'Posts after approval', auto: 'Posts by itself' };
const TABS = [['posts', 'Posts', LayoutList], ['clips', 'Clips', Scissors], ['studio', 'Studio', Clapperboard], ['kit', 'Brand Kit', BookOpen], ['settings', 'Platforms & settings', Settings2]];
const initialTab = () => { const t = new URLSearchParams(window.location.search).get('tab'); return TABS.some(([k]) => k === t) ? t : 'posts'; };

const errText = (e, fallback) => e?.response?.data?.detail || fallback;
const timeET = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) + ' ET'; } catch (e) { return ''; }
};
const dayLabel = (date, today) => {
  if (date === today) return 'Today';
  try { return new Date(`${date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }); } catch (e) { return date; }
};
const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch (e) { /* older browsers */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
};

const MemberPicker = ({ api, onPick }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRows([]); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`${BASE}/members`, { params: { q: term } });
        if (mine === seq.current) setRows(r.data.members || []);
      } catch (e) { /* the list just stays empty */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, api]);
  return (
    <div className="relative max-w-md">
      <div className="flex items-center border rounded-lg px-2 bg-white">
        <Search className="w-3.5 h-3.5 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a member by name or @handle" className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent" data-testid="tourmaline-release-search" />
      </div>
      {rows.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {rows.map((m) => (
            <button key={m.id} disabled={m.clip_release_signed} onClick={() => { onPick(m); setQ(''); setRows([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50">
              {m.picture ? <img src={m.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : <span className="w-6 h-6 rounded-full bg-gray-200" />}
              <span className="font-medium text-gray-900">{m.name}</span><span className="text-gray-400">@{m.handle}</span>
              {m.clip_release_signed && <span className="ml-auto text-xs text-emerald-700">on file</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TourmalinePage = () => {
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [zoom, setZoom] = useState(null);        // a post whose picture is open
  const [posted, setPosted] = useState(null);    // {post, platform, url}
  const [handles, setHandles] = useState({});
  const [metaPick, setMetaPick] = useState(null); // {pending_id, pages} — back from Facebook's sign-in
  const [tab, setTab] = useState(initialTab);     // Round 56

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${BASE}/overview`);
      setOv(r.data); setCfg(r.data.config); setDirty(false);
      setHandles(Object.fromEntries((r.data.platforms || []).map((p) => [p.key, p.handle || ''])));
    } catch (e) { toast.error('Could not load Tourmaline'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // Round 49: Facebook sends the admin back here with ?meta=<id> (pick the Page) or ?meta_error=<why>
  // Round 57: X sends the admin back with ?x=<account> (connected) or ?x_error=<why>
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const id = q.get('meta');
    const err = q.get('meta_error');
    const x = q.get('x');
    const xErr = q.get('x_error');
    if (!id && !err && !x && !xErr) return;
    setTab('settings');
    window.history.replaceState(null, '', window.location.pathname);      // drop the one-time id from the address bar
    if (xErr) { toast.error(xErr, { duration: 12000 }); return; }
    if (x) { toast.success(`Connected to X as ${x}`); return; }
    if (err) { toast.error(err, { duration: 12000 }); return; }
    api.get(`${BASE}/meta/pending/${id}`)
      .then((r) => setMetaPick(r.data))
      .catch((e) => toast.error(errText(e, 'That connection attempt expired — press Connect again')));
  }, [api]);

  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok); await load(); return r; }
    catch (e) { toast.error(errText(e, 'That did not work')); return null; }
    finally { setBusy(''); }
  };

  if (!ov || !cfg) return <div className="p-6 text-sm text-gray-500">Loading Tourmaline…</div>;

  const set = (k, v) => { setCfg((c) => ({ ...c, [k]: v })); setDirty(true); };
  const imageUrl = (post) => `${API_BASE_URL}${post.image_path}`;
  const byDate = ov.posts.reduce((acc, p) => { (acc[p.date] = acc[p.date] || []).push(p); return acc; }, {});
  const dates = Object.keys(byDate).sort().reverse();
  const anyConnected = ov.platforms.some((p) => p.connected);

  const planNow = () => call('plan', () => api.post(`${BASE}/plan-now`), (r) => {
    const d = r.data || {};
    if (d.reason) return `Nothing drafted: ${d.reason}`;
    if (!d.created) return 'Today is already drafted';
    return `${d.proposed} sent to Decisions${d.blocked ? ` · ${d.blocked} blocked by Lapis` : ''}`;
  });
  const copy = async (text, what) => {
    if (await copyText(text)) toast.success(`${what} copied`);
    else toast.error('Could not copy — select the text and copy it by hand');
  };
  const connect = async (p) => {
    setBusy('connect');
    try {
      const r = await api.post(`${BASE}/${p.vendor === 'x' ? 'x' : 'meta'}/start`);
      window.location.assign(r.data.url);
    } catch (e) { toast.error(errText(e, 'Could not start the connection')); setBusy(''); }
  };
  const vendorStatus = (p) => (p.vendor === 'x' ? ov.x : ov.meta) || { configured: false, missing: [] };
  const pickPage = (page) => call('pick', () => api.post(`${BASE}/meta/connect`, { pending_id: metaPick.pending_id, page_id: page.page_id }),
    (r) => r.data.note || `Connected: ${r.data.page}${r.data.instagram ? ` and @${r.data.instagram}` : ''}`).then(() => setMetaPick(null));
  const setMode = (p, mode) => {
    if (mode === 'auto' && !window.confirm(`Let Tourmaline post on ${p.label} by itself?\n\nPosts will go out at their time without waiting in Decisions. Lapis and the release check still read every one. You can switch back at any time.`)) return;
    call(`mode:${p.key}`, () => api.post(`${BASE}/platforms/${p.key}`, { mode }), `${p.label}: ${MODE_LABEL[mode].toLowerCase()}`);
  };
  const disconnect = (p) => window.confirm(`Disconnect ${p.label}?\n\nTourmaline forgets its access to the account and goes back to preparing posts for you to publish.`)
    && call(`dc:${p.key}`, () => api.post(`${BASE}/platforms/${p.key}/disconnect`), `${p.label} disconnected`);
  const publishNow = (post, key, label) => call(`pub:${post.id}:${key}`, () => api.post(`${BASE}/posts/${post.id}/publish-now`, { platform: key }),
    (r) => (r.data.status === 'posted' ? `Posted on ${label}` : `${label}: ${r.data.error || r.data.status}`));
  const markPosted = () => call('posted', () => api.post(`${BASE}/posts/${posted.post.id}/posted`, { platform: posted.platform, url: posted.url.trim() }), 'Marked as posted')
    .then((r) => { if (r) setPosted(null); });

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="tourmaline-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Share2 className="w-5 h-5 text-pink-600" /> Tourmaline · Social Media & Content</h2>
          <p className="text-sm text-gray-500">
            {ov.counts.waiting} waiting in Decisions · {ov.counts.ready} ready to post · {ov.counts.posted_7d} posted this week
            {ov.counts.blocked > 0 && <span className="text-red-600"> · {ov.counts.blocked} blocked</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {ov.counts.waiting > 0 && (
            <button onClick={() => navigate('/decisions')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600" data-testid="tourmaline-open-decisions">
              <Inbox className="w-4 h-4" /> Review in Decisions
            </button>
          )}
          <button onClick={() => window.confirm("Throw away today's unpublished drafts and draft the day again?\n\nTourmaline looks at the pictures first this time. Anything already posted stays.") && call('redraft', () => api.post(`${BASE}/redraft-today`), (r) => `${r.data.deleted || 0} gone · ${r.data.looked || 0} pictures looked at · ${r.data.proposed || 0} new drafts in Decisions`)}
            disabled={busy === 'redraft'} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-pink-300 text-pink-700 text-sm font-semibold hover:bg-pink-50 disabled:opacity-50" data-testid="tourmaline-redraft">
            <RefreshCw className="w-4 h-4" /> {busy === 'redraft' ? 'Redrafting…' : 'Redraft today'}
          </button>
          <button onClick={planNow} disabled={busy === 'plan'} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-pink-600 text-white text-sm font-semibold hover:bg-pink-700 disabled:opacity-50" data-testid="tourmaline-plan-now">
            <Sparkles className="w-4 h-4" /> {busy === 'plan' ? 'Drafting…' : "Draft today's posts now"}
          </button>
          <button onClick={load} className="p-2 rounded-lg border text-gray-600 hover:bg-gray-50" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b" data-testid="tourmaline-tabs">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === key ? 'border-pink-600 text-pink-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-testid={`tourmaline-tab-${key}`}>
            <Icon className="w-4 h-4" /> {label}{key === 'posts' && ov.counts.waiting > 0 ? ` (${ov.counts.waiting})` : ''}
          </button>
        ))}
      </div>

      {tab === 'clips' && <ClipsPanel api={api} />}
      {tab === 'studio' && <TourmalineStudio api={api} platforms={ov.platforms} />}
      {tab === 'kit' && <BrandKitPanel api={api} onChanged={load} />}

      {tab === 'posts' && !anyConnected && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <b>You publish, Tourmaline prepares.</b> Every morning at {((cfg.plan_hour + 11) % 12) + 1}:00 {cfg.plan_hour < 12 ? 'AM' : 'PM'} ET the day's posts land in Decisions.
          Approve one and it shows up here as <i>ready to post</i>: copy the caption, download the picture, publish it on the platform, then mark it posted.
          No account is connected yet, so nothing can be published from here — connect Facebook & Instagram under Platforms when you want Tourmaline to publish what you approve.
        </div>
      )}

      {/* The week */}
      {tab === 'posts' && <section className="space-y-4">
        <h3 className="font-semibold text-gray-900">This week</h3>
        {dates.length === 0 && <p className="text-sm text-gray-500 border rounded-xl px-4 py-6 text-center bg-white">No posts yet. Press “Draft today's posts now”, or start the agent and it drafts every morning.</p>}
        {dates.map((date) => (
          <div key={date}>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{dayLabel(date, ov.today)}</p>
            <div className="grid gap-3 lg:grid-cols-2">
              {byDate[date].map((post) => {
                const [label, chip] = POST_STATUS[post.status] || [post.status, 'bg-gray-100 text-gray-600'];
                return (
                  <div key={post.id} className="bg-white border rounded-xl p-3 flex gap-3" data-testid={`tourmaline-post-${post.id}`}>
                    <button onClick={() => setZoom(post)} className="shrink-0" title="Open the picture">
                      <img src={imageUrl(post)} alt="" loading="lazy" className="w-28 h-28 rounded-lg object-cover border" />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-pink-700">{post.label}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${chip}`}>{label}</span>
                        <span className="text-[11px] text-gray-400">suggested {timeET(post.scheduled_for)}</span>
                        {post.llm && <span className="text-[11px] text-violet-600">opening line by Claude</span>}
                        {post.picture && (
                          <span className="text-[11px] text-gray-500" title={post.picture.description || ''}>
                            on “{post.picture.title}”{post.picture.game ? ` · ${post.picture.game}` : ''}{post.picture.scene ? ` · ${post.picture.scene}` : ''}{post.picture.seen ? '' : ' · not looked at yet'}
                          </span>
                        )}
                        {post.media?.kind === 'video' && <span className="text-[11px] text-violet-700">video · {post.media.duration}s</span>}
                        {post.source?.kind === 'studio' && <span className="text-[11px] text-pink-600">Studio</span>}
                      </div>
                      <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap line-clamp-5">{post.text}</p>
                      {post.blocked_reason && <p className="mt-1 text-xs text-red-700 flex gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {post.blocked_reason}</p>}
                      {(post.warnings || []).length > 0 && post.status !== 'blocked' && <p className="mt-1 text-xs text-amber-700">Lapis notes: {post.warnings.join('; ')}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <a href={`${imageUrl(post)}?download=1`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border text-gray-700 hover:bg-gray-50"><Download className="w-3.5 h-3.5" /> Picture</a>
                        {post.media?.kind === 'video' && <a href={`${API_BASE_URL}${post.media.path}?download=1`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border text-gray-700 hover:bg-gray-50"><Download className="w-3.5 h-3.5" /> Video</a>}
                        {post.status === 'proposed' && <button onClick={() => navigate('/decisions')} className="text-xs px-2 py-1 rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50">Approve in Decisions</button>}
                        {['proposed', 'blocked', 'rejected', 'expired'].includes(post.status) && (
                          <button onClick={() => window.confirm('Delete this draft for good?') && call(`del:${post.id}`, () => api.post(`${BASE}/posts/${post.id}/delete`), 'Deleted')} disabled={busy === `del:${post.id}`}
                            className="text-xs px-2 py-1 rounded-md border text-gray-500 hover:text-red-700 hover:border-red-300" data-testid={`tourmaline-delete-${post.id}`}>Delete</button>
                        )}
                      </div>
                      {['approved', 'done'].includes(post.status) && (
                        <div className="mt-2 border-t pt-2 space-y-1.5">
                          {Object.entries(post.platforms).map(([key, row]) => {
                            const p = ov.platforms.find((x) => x.key === key) || { label: key };
                            const text = (post.variants || {})[key] || post.text;
                            return (
                              <div key={key} className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="w-28 font-medium text-gray-800">{p.label}</span>
                                <span className={row.status === 'posted' ? 'text-emerald-700' : row.status === 'failed' || row.status === 'blocked' ? 'text-red-600' : 'text-gray-500'}>
                                  {PLATFORM_STATUS[row.status] || row.status}{row.status === 'posted' && row.manual === false ? ' by Tourmaline' : ''}
                                </span>
                                {row.url && <a href={row.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">open</a>}
                                {row.engagement && <span className="text-gray-500">{row.engagement.likes} likes · {row.engagement.comments} {key === 'x' ? 'replies' : 'comments'}{row.engagement.shares != null ? ` · ${row.engagement.shares} ${key === 'x' ? 'reposts' : 'shares'}` : ''}{row.engagement.views != null ? ` · ${row.engagement.views} views` : ''}</span>}
                                {row.error && <span className="text-red-600">{row.error}</span>}
                                {(row.status === 'scheduled' || (row.status === 'failed' && p.effective_mode && p.effective_mode !== 'draft_only')) && (
                                  <button onClick={() => publishNow(post, key, p.label)} disabled={busy === `pub:${post.id}:${key}`}
                                    className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50" data-testid={`tourmaline-publish-now-${key}`}>
                                    <Send className="w-3.5 h-3.5" /> {busy === `pub:${post.id}:${key}` ? 'Publishing…' : row.status === 'failed' ? 'Try again' : 'Publish now'}
                                  </button>
                                )}
                                {['ready', 'failed'].includes(row.status) && (
                                  <span className={`${row.status === 'failed' && p.effective_mode && p.effective_mode !== 'draft_only' ? '' : 'ml-auto '}flex items-center gap-1.5`}>
                                    <button onClick={() => copy(text, `${p.label} caption`)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-gray-50" data-testid={`tourmaline-copy-${key}`}><Copy className="w-3.5 h-3.5" /> Copy caption</button>
                                    <button onClick={() => setPosted({ post, platform: key, label: p.label, url: '' })} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle className="w-3.5 h-3.5" /> Mark posted</button>
                                    <button onClick={() => call(`skip:${post.id}:${key}`, () => api.post(`${BASE}/posts/${post.id}/skip`, { platform: key }), 'Skipped')} className="px-2 py-1 rounded-md border text-gray-500 hover:bg-gray-50">Skip</button>
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>}

      {/* Platforms */}
      {tab === 'settings' && <section>
        <h3 className="font-semibold text-gray-900 mb-2">Platforms</h3>
        <div className="bg-white border rounded-xl divide-y">
          {ov.platforms.map((p) => (
            <div key={p.key} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-2 w-44">
                <input type="checkbox" checked={p.enabled} disabled={p.needs === 'video' || busy === `on:${p.key}`}
                  onChange={(e) => call(`on:${p.key}`, () => api.post(`${BASE}/platforms/${p.key}`, { enabled: e.target.checked }), `${p.label}: ${e.target.checked ? 'on' : 'off'}`)} data-testid={`tourmaline-platform-${p.key}`} />
                <span className="font-medium text-gray-900">{p.label}</span>
              </label>
              <input value={handles[p.key] || ''} onChange={(e) => setHandles((h) => ({ ...h, [p.key]: e.target.value }))}
                onBlur={() => (handles[p.key] || '') !== (p.handle || '') && call(`h:${p.key}`, () => api.post(`${BASE}/platforms/${p.key}`, { handle: handles[p.key] || '' }), 'Saved')}
                placeholder="@account" className="border rounded-lg px-2 py-1 text-sm w-40" />
              {p.connected ? (
                <select value={p.mode} disabled={busy === `mode:${p.key}`} onChange={(e) => setMode(p, e.target.value)} className="border rounded-lg px-2 py-1 text-xs bg-white" data-testid={`tourmaline-mode-${p.key}`}>
                  <option value="draft_only">{MODE_LABEL.draft_only}</option>
                  <option value="propose">{MODE_LABEL.propose}</option>
                  <option value="auto" disabled={!p.auto_unlocked}>{MODE_LABEL.auto}{p.auto_unlocked ? '' : ` (from ${new Date(p.auto_unlock_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`}</option>
                </select>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700"><Lock className="w-3 h-3" /> {MODE_LABEL.draft_only}</span>
              )}
              <span className="text-xs text-gray-500">
                {p.needs === 'video' ? 'Needs a video — switches on when there are clips'
                  : p.connected ? `Connected${p.account_name ? ` as ${p.account_name}` : ''}`
                    : p.account_status === 'expired' ? <span className="text-red-600">Needs connecting again — its access was changed or removed</span>
                      : p.can_connect ? 'Not connected' : 'Not connected — captions up to ' + p.limit.toLocaleString() + ' characters'}
              </span>
              {p.can_connect && (
                <span className="ml-auto flex items-center gap-2">
                  {p.connected && <button onClick={() => disconnect(p)} className="text-xs px-2 py-1 rounded-md border text-gray-600 hover:bg-gray-50">Disconnect</button>}
                  {!p.connected && (
                    <button onClick={() => connect(p)} disabled={busy === 'connect' || !vendorStatus(p).configured} title={vendorStatus(p).configured ? '' : `Missing: ${(vendorStatus(p).missing || []).join(', ')}`}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-40" data-testid={`tourmaline-connect-${p.key}`}>
                      <Link2 className="w-3.5 h-3.5" /> {busy === 'connect' ? 'Opening…' : 'Connect'}
                    </button>
                  )}
                </span>
              )}
            </div>
          ))}
        </div>
        {!ov.meta?.configured && (
          <p className="text-xs text-amber-700 mt-2">Facebook &amp; Instagram: Connect is off until these keys are set on the server: {(ov.meta?.missing || []).join(', ') || 'META_APP_ID, META_APP_SECRET, SOCIAL_TOKEN_KEY'}.</p>
        )}
        {!ov.x?.configured && (
          <p className="text-xs text-amber-700 mt-2">X: Connect is off until these keys are set on the server: {(ov.x?.missing || []).join(', ') || 'X_CLIENT_ID, X_CLIENT_SECRET, SOCIAL_TOKEN_KEY'} (the X developer app's OAuth 2.0 client, callback {ov.x?.callback_path || '/api/social/x/callback'}).</p>
        )}
        <p className="text-xs text-gray-500 mt-2">One Connect covers the Facebook Page and the Instagram account linked to it; X connects on its own row — you sign in on the platform's own page, GEMS never sees a password. A connected platform stays on “{MODE_LABEL.draft_only.toLowerCase()}” until you choose “{MODE_LABEL.propose.toLowerCase()}”; “{MODE_LABEL.auto.toLowerCase()}” opens {ov.lock_days} days after connecting. LinkedIn is still by hand.</p>
      </section>}

      {/* Settings */}
      {tab === 'settings' && <section>
        <h3 className="font-semibold text-gray-900 mb-2">The daily plan</h3>
        <div className="bg-white border rounded-xl p-4 grid gap-4 sm:grid-cols-2">
          <label className="text-sm text-gray-700">Posts a day
            <select value={cfg.daily_posts} onChange={(e) => set('daily_posts', parseInt(e.target.value, 10))} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-28">
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="block text-xs text-gray-400 mt-1">Suggested times: {ov.slot_times.map((t) => { const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; }).join(' · ')} ET</span>
          </label>
          <label className="text-sm text-gray-700">Draft the day at (hour, ET)
            <input type="number" min={0} max={23} value={cfg.plan_hour} onChange={(e) => set('plan_hour', parseInt(e.target.value, 10) || 0)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-28" />
          </label>
          <label className="text-sm text-gray-700 sm:col-span-2">Closing line (every caption and picture ends with it)
            <input value={cfg.closing_line} maxLength={120} onChange={(e) => set('closing_line', e.target.value)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" data-testid="tourmaline-closing-line" />
          </label>
          <label className="text-sm text-gray-700 sm:col-span-2">Consent — what counts as a member's yes
            <select value={cfg.consent_mode || 'terms_plus_release'} onChange={(e) => set('consent_mode', e.target.value)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full bg-white" data-testid="tourmaline-consent">
              {Object.entries(ov.consent_modes || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <span className="block text-xs text-gray-400 mt-1">The Terms (§12, from the {ov.terms_featuring_version} version) let GEMS feature a member's content with their name and likeness as they appear. Members who accepted that version count as having said yes to being named and shown. Changing a face (Design) or making someone move and speak (Motion) is more than the Terms say, which is why the recommended setting asks for a written release there.</span>
          </label>
          <label className="text-sm text-gray-700 sm:col-span-2">Brain — the Claude model Tourmaline writes and looks with
            <select value={cfg.model || 'claude-sonnet-5-5'} onChange={(e) => set('model', e.target.value)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full bg-white" data-testid="tourmaline-model">
              {Object.entries(ov.models || { 'claude-sonnet-5-5': 'Sonnet 5.5' }).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <span className="block text-xs text-gray-400 mt-1">{ov.llm ? 'A key is set.' : 'No Claude key on the server yet — templates only until the owner adds ANTHROPIC_API_KEY.'} Sonnet 5.5 reads a picture for about a cent; Opus and Fable cost several times more for the same caption.</span>
          </label>
          <label className="text-sm text-gray-700">Friday scoreboard needs at least this many games in the week
            <input type="number" min={1} value={cfg.min_scoreboard_games} onChange={(e) => set('min_scoreboard_games', parseInt(e.target.value, 10) || 1)} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-28" />
          </label>
          <div className="space-y-2 text-sm text-gray-700">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!cfg.admin_push} onChange={(e) => set('admin_push', e.target.checked)} /> Notify the admins when the day's drafts are ready</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!cfg.use_llm} onChange={(e) => set('use_llm', e.target.checked)} /> Let Claude add an opening line
              <span className="text-xs text-gray-400">({ov.llm ? `on · $${(ov.cap?.spent || 0).toFixed(2)} of $${(ov.cap?.cap || 0).toFixed(2)} this month` : 'no key set — templates only'})</span>
            </label>
          </div>
          <div className="sm:col-span-2">
            <button disabled={!dirty || busy === 'cfg'} onClick={() => call('cfg', () => api.post(`${BASE}/config`, cfg), 'Saved')} className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-40" data-testid="tourmaline-save-config">Save</button>
          </div>
        </div>
      </section>}

      {/* Releases */}
      {tab === 'settings' && <section>
        <h3 className="font-semibold text-gray-900 mb-1">Clip releases</h3>
        <p className="text-sm text-gray-500 mb-2">A written release recorded here is a member's own yes, beyond the Terms: it is what Design and Motion ask for before they change a member's face or voice (under the recommended consent setting), and it covers members who have not accepted the current Terms. A text message saying yes is enough; record it once.</p>
        <MemberPicker api={api} onPick={(m) => call(`rel:${m.id}`, () => api.post(`${BASE}/releases`, { user_id: m.id, signed: true }), `Release recorded for @${m.handle}`)} />
        <div className="mt-3 bg-white border rounded-xl divide-y">
          {ov.releases.length === 0 && <p className="px-4 py-3 text-sm text-gray-500">No releases on file. Posts will not name any member.</p>}
          {ov.releases.map((m) => (
            <div key={m.id} className="px-4 py-2 flex items-center gap-3 text-sm">
              {m.picture ? <img src={m.picture} alt="" className="w-7 h-7 rounded-full object-cover" /> : <span className="w-7 h-7 rounded-full bg-gray-200" />}
              <span className="font-medium text-gray-900">{m.name}</span><span className="text-gray-400">@{m.handle}</span>
              <button onClick={() => window.confirm(`Withdraw the release for @${m.handle}? Posts that name them can no longer be approved or published.`) && call(`rel:${m.id}`, () => api.post(`${BASE}/releases`, { user_id: m.id, signed: false }), 'Release withdrawn')}
                className="ml-auto text-xs px-2 py-1 rounded-md border text-gray-600 hover:bg-gray-50">Withdraw</button>
            </div>
          ))}
        </div>
      </section>}

      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setZoom(null)}>
          <div className="bg-white rounded-xl p-3 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            {zoom.media?.kind === 'video' ? <video src={`${API_BASE_URL}${zoom.media.path}`} poster={imageUrl(zoom)} controls className="w-full rounded-lg bg-black max-h-[70vh]" /> : <img src={imageUrl(zoom)} alt="" className="w-full rounded-lg" />}
            <div className="flex items-center justify-between mt-3">
              <a href={`${imageUrl(zoom)}?download=1`} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-gray-900 text-white"><Download className="w-4 h-4" /> Download picture</a>
              <button onClick={() => setZoom(null)} className="text-gray-500 hover:text-gray-800"><X className="w-5 h-5" /></button>
            </div>
          </div>
        </div>
      )}

      {metaPick && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" data-testid="tourmaline-page-picker">
            <h3 className="font-semibold text-gray-900">Which Page is GEMS?</h3>
            <p className="text-sm text-gray-600">Tourmaline will only ever post on the Page you choose here, and on the Instagram account linked to it.</p>
            <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
              {metaPick.pages.map((pg) => (
                <button key={pg.page_id} disabled={!pg.can_post || busy === 'pick'} onClick={() => pickPage(pg)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 disabled:opacity-50">
                  <span className="font-medium text-gray-900">{pg.name}</span>
                  <span className="block text-xs text-gray-500">
                    {!pg.can_post ? 'You cannot create posts on this Page' : pg.instagram ? `Instagram: @${pg.instagram.username}` : 'No Instagram professional account linked — the Page only'}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex justify-end"><button onClick={() => setMetaPick(null)} className="px-3 py-2 rounded-lg border text-sm">Cancel</button></div>
          </div>
        </div>
      )}

      {posted && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPosted(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900">Posted on {posted.label}?</h3>
            <p className="text-sm text-gray-600">Paste the link to the post if you have it (optional). It is kept with the post for later.</p>
            <input value={posted.url} onChange={(e) => setPosted((s) => ({ ...s, url: e.target.value }))} placeholder="https://…" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="tourmaline-posted-url" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setPosted(null)} className="px-3 py-2 rounded-lg border text-sm">Cancel</button>
              <button onClick={markPosted} disabled={busy === 'posted'} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">Mark as posted</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourmalinePage;
